import type { FC, MouseEvent } from 'react';
import { Link } from 'react-router';
import { Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import {
  getArtifactDisplayTitle,
  getArtifactStatusLabel,
  getArtifactStatusTone,
  getArtifactTypeIcon,
  getArtifactTypeLabel,
  resolveArtifactUrl,
  shouldDisplayArtifactStatus,
} from './artifactDisplay';

import * as styles from './ArtifactChip.css';

// The artifact detail page (O11Y-1492) hasn't registered its route yet. Build the
// path inline so we don't have to add a route entry to appRoutes.tsx in this PR.
const buildArtifactPath = (projectKey: string, type: string, externalId: string): string =>
  `/projects/${encodeURIComponent(projectKey)}/vega/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(externalId)}`;

export interface ArtifactChipProps {
  projectKey: string;
  type: string;
  externalId: string;
  title?: string | null;
  status?: string | null;
  url?: string | null;
  // `inline` renders the pill shape suited to embedding in a sentence; `block` is the
  // full-width row used in the artifact rail.
  variant?: 'inline' | 'block';
  // When true, the chip styles itself as a not-yet-hydrated placeholder. The lazy-fetch
  // path uses this while loading and on error so the chip still occupies the same space.
  placeholder?: boolean;
}

export const ArtifactChip: FC<ArtifactChipProps> = ({
  projectKey,
  type,
  externalId,
  title,
  status,
  url,
  variant = 'inline',
  placeholder = false,
}) => {
  const displayTitle = getArtifactDisplayTitle(type, title, externalId);
  const typeLabel = getArtifactTypeLabel(type);
  const showStatus = shouldDisplayArtifactStatus(type, status);
  const statusLabel = showStatus && status ? getArtifactStatusLabel(status) : '';
  const statusTone = showStatus && status ? getArtifactStatusTone(status) : null;
  const icon = getArtifactTypeIcon(type);
  const className = [
    styles.chip,
    variant === 'block' ? styles.chipBlock : '',
    placeholder ? styles.chipPlaceholder : '',
  ]
    .filter(Boolean)
    .join(' ');

  const safeUrl = resolveArtifactUrl(type, url);
  const tooltipText = statusLabel ? `${typeLabel}: ${displayTitle} · ${statusLabel}` : `${typeLabel}: ${displayTitle}`;

  const handleExternalClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!safeUrl) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <TooltipTrigger delay={500}>
      <Link to={buildArtifactPath(projectKey, type, externalId)} className={className}>
        <Icon name={icon} size="small" className={styles.chipIcon} />
        {variant === 'block' ? <span className={styles.chipType}>{typeLabel}</span> : null}
        <span className={styles.chipTitle}>{displayTitle}</span>
        {statusLabel && statusTone ? (
          <span className={`${styles.chipStatus} ${styles.chipStatusTone[statusTone]}`}>{statusLabel}</span>
        ) : null}
        {safeUrl ? (
          // Rendered as a button rather than a nested <a> because the surrounding <Link>
          // already produces an anchor — nesting anchors is invalid HTML and browsers
          // unwrap them, breaking event propagation.
          <button
            type="button"
            onClick={handleExternalClick}
            className={styles.chipExternalButton}
            aria-label={`Open ${typeLabel} externally`}
          >
            <Icon name="link-external" size="small" className={styles.chipIcon} />
          </button>
        ) : null}
      </Link>
      <Tooltip placement="top">{tooltipText}</Tooltip>
    </TooltipTrigger>
  );
};
